/**
 * Where Stripe Checkout sends the browser back after a successful payment.
 *
 * Mounted at the top level as /signup/payment-complete, matching the
 * success_url SelfServeSignupService.submit builds
 * (`${customerBaseUrl}/signup/payment-complete?session_id={CHECKOUT_SESSION_ID}`).
 *
 * Provisioning happens off a Stripe webhook, so the account may not exist yet
 * when this redirect lands. Rather than claim either way, the page asks
 * GET /api/self-serve/signup/progress every few seconds for about a minute and
 * shows what is actually true: still setting up, ready, or handed to a person.
 * That endpoint returns only a coarse state - no name, email or amount - keyed
 * by the session id only this browser holds.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import axios from '../../../../axiosConfig';
import StandaloneShell from '../../components/standalone-shell';
import { SELF_SERVE_CONTACT_EMAIL } from '../../utils/contact';
import { useSelfServeConfig } from '../../config';
import { FLEX_COPY } from '../../utils/flexCopy';

type Progress = 'SETTING_UP' | 'READY' | 'NEEDS_HELP';
type View = 'checking' | 'ready' | 'help' | 'pending';

export const POLL_MS = 3000;
export const MAX_POLLS = 20;

const PaymentComplete = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [view, setView] = useState<View>(sessionId ? 'checking' : 'pending');
  const config = useSelfServeConfig();
  // What Stripe just did depends on the flow: a card saved (card on file) or a payment taken (down payment). Until the
  // flow is known, and when signup is closed (a session started before it closed), the heading says neither.
  const receivedTitle =
    config.status !== 'ready'
      ? 'Thanks'
      : config.mode === 'cardOnFile'
        ? FLEX_COPY.cardSavedTitle
        : config.mode === 'legacy'
          ? 'Payment received'
          : 'Thanks';

  useEffect(() => {
    if (!sessionId) return undefined;
    let polls = 0;
    let cancelled = false;
    let timer: number | undefined;

    const check = async () => {
      polls += 1;
      try {
        const res = await axios.get<{ status: Progress }>(
          '/api/self-serve/signup/progress',
          { params: { sessionId } },
        );
        if (cancelled) return;
        if (res.data.status === 'READY') {
          setView('ready');
          return;
        }
        if (res.data.status === 'NEEDS_HELP') {
          setView('help');
          return;
        }
      } catch {
        // A 404 (session unknown) or a network error: keep trying until the
        // budget runs out, then fall back to the honest "we'll email you".
        if (cancelled) return;
      }
      if (polls >= MAX_POLLS) {
        setView('pending');
        return;
      }
      timer = window.setTimeout(check, POLL_MS);
    };

    check();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <StandaloneShell>
      {view === 'checking' && (
        <Result
          icon={<Spin size="large" />}
          title={receivedTitle}
          subTitle="Setting up your account. This usually takes a few seconds."
        />
      )}
      {view === 'ready' && (
        <Result
          status="success"
          title="Your account is ready"
          subTitle="We've emailed you a link to set your password. It works for 24 hours. Your order confirmation is on its way too."
          extra={[
            <Button key="login" type="primary" href="/auth/login">
              Go to login
            </Button>,
            <Button key="new-link" type="link" href="/auth/reset-password">
              Didn&apos;t get the email? Request a new link
            </Button>,
          ]}
        />
      )}
      {view === 'help' && (
        <Result
          status="info"
          title={receivedTitle}
          subTitle={`We need to finish setting up your account by hand, and we've been notified. We'll email you shortly. Questions: ${SELF_SERVE_CONTACT_EMAIL}`}
        />
      )}
      {view === 'pending' && (
        <Result
          status="success"
          title={receivedTitle}
          subTitle={`We're finishing setting up your account. Check your email in the next few minutes for a link to set your password. If nothing arrives, contact ${SELF_SERVE_CONTACT_EMAIL}.`}
        />
      )}
    </StandaloneShell>
  );
};

export default PaymentComplete;
